import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import "./AdminVoucherRedeem.css";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminVoucherRedeem() {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { userInfo, authFetch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isAddMode, setIsAddMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [pointsRequired, setPointsRequired] = useState("");
  const [minimumSpendAmount, setMinimumSpendAmount] = useState("");
  const [expiryDays, setExpiryDays] = useState("30");

  const [templateSearch, setTemplateSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/api/VoucherTemplates`);
        if (response.ok) {
          const data = await response.json();
          setTemplates(data || []);
          setFilteredTemplates(data || []);
          setError(""); 
        } else {
          if (response.status === 404) {
            setTemplates([]);
            setFilteredTemplates([]);
            setError("");
          } else {
            setError("Failed to fetch voucher templates");
          }
        }
      } catch (err) {
        setError("Error fetching voucher templates: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    let result = templates;

    if (templateSearch.trim() !== '') {
      const searchTerm = templateSearch.toLowerCase();
      result = result.filter(template =>
        template.name.toLowerCase().includes(searchTerm) ||
        template.description.toLowerCase().includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      result = result.filter(template => template.isActive === isActive);
    }

    setFilteredTemplates(result);
  }, [templateSearch, statusFilter, templates]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: '#45a049',
      timer: 1500
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      confirmButtonColor: '#e74c3c'
    });
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setDiscountAmount("");
    setPointsRequired("");
    setMinimumSpendAmount("");
    setExpiryDays("30");
    setCurrentTemplate(null);
    setFormError("");
  };

  const handleAddClick = () => {
    resetForm();
    setIsAddMode(true);
    setIsEditMode(false);
    setShowModal(true);
  };

  const handleEditClick = (template) => {
    setCurrentTemplate(template);
    setName(template.name);
    setDescription(template.description);
    setDiscountAmount(template.discountAmount);
    setPointsRequired(template.pointsRequired);
    setMinimumSpendAmount(template.minimumSpendAmount || "");
    setExpiryDays(template.expiryDays || "30");

    setIsEditMode(true);
    setIsAddMode(false);
    setShowModal(true);
  };

  const handleCancelClick = () => {
    setIsAddMode(false);
    setIsEditMode(false);
    resetForm();
    setShowModal(false);
  };

  const resetSearchFilters = () => {
    setTemplateSearch("");
    setStatusFilter("all");
  };

  const validateForm = () => {
    if (!name.trim()) return "Template name is required";
    if (!description.trim()) return "Description is required";
    if (
      !discountAmount ||
      isNaN(discountAmount) ||
      parseFloat(discountAmount) <= 0
    )
      return "Discount amount must be a positive number";
    if (
      !pointsRequired ||
      isNaN(pointsRequired) ||
      parseInt(pointsRequired) <= 0
    )
      return "Points required must be a positive number";
    if (
      !expiryDays ||
      isNaN(expiryDays) ||
      parseInt(expiryDays) < 1 ||
      parseInt(expiryDays) > 365
    )
      return "Expiry days must be between 1 and 365 days";
    if (minimumSpendAmount && (isNaN(minimumSpendAmount) || parseFloat(minimumSpendAmount) < 0))
      return "Minimum spend amount must be a non-negative number";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setFormError("");

    const templateData = {
      name: name.trim(),
      description: description.trim(),
      discountAmount: parseFloat(discountAmount),
      isPercentage: false,
      pointsRequired: parseInt(pointsRequired),
      minimumSpendAmount: minimumSpendAmount ? parseFloat(minimumSpendAmount) : null,
      expiryDays: parseInt(expiryDays),
    };

    try {
      let url = `${API_BASE_URL}/api/VoucherTemplates`;
      let method = "POST";

      if (isEditMode) {
        url = `${API_BASE_URL}/api/VoucherTemplates/${currentTemplate.templateId}`;
        method = "PUT";
        templateData.templateId = currentTemplate.templateId;
        templateData.isActive = currentTemplate.isActive;
      }

      const response = await authFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        const successMessage = isEditMode
          ? "Voucher template updated successfully!"
          : "Voucher template created successfully!"
        showSuccessAlert(successMessage);

        const refreshResponse = await authFetch(
          `${API_BASE_URL}/api/VoucherTemplates`
        );
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setTemplates(refreshData || []);
          setFilteredTemplates(refreshData || []);
        }

        setIsAddMode(false);
        setIsEditMode(false);
        resetForm();
        setShowModal(false);
      } else {
        const errorData = await response.json();
        setFormError(errorData.message || "Failed to save voucher template");
      }
    } catch (err) {
      setFormError("Error saving voucher template: " + err.message);
    }
  };

  const handleToggleActive = async (templateId, isCurrentlyActive) => {
    const result = await Swal.fire({
      title: isCurrentlyActive ? 'Deactivate Template?' : 'Reactivate Template?',
      text: isCurrentlyActive
        ? 'Are you sure you want to deactivate this voucher template?'
        : 'Are you sure you want to reactivate this voucher template?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isCurrentlyActive ? '#e74c3c' : '#45a049',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: 'Yes'
    });

    if (result.isConfirmed) {
      try {
        let url, method;

        if (isCurrentlyActive) {
          url = `${API_BASE_URL}/api/VoucherTemplates/${templateId}`;
          method = "DELETE";
        } else {
          url = `${API_BASE_URL}/api/VoucherTemplates/${templateId}/reactivate`;
          method = "POST";
        }

        const response = await authFetch(url, { method });

        if (response.ok) {
          const action = isCurrentlyActive ? "deactivated" : "reactivated";
          showSuccessAlert(`Voucher template ${action} successfully!`);

          const updatedTemplates = templates.map((t) =>
            t.templateId === templateId ? { ...t, isActive: !isCurrentlyActive } : t
          );
          setTemplates(updatedTemplates);
          setFilteredTemplates(updatedTemplates);
        } else {
          const action = isCurrentlyActive ? "deactivate" : "reactivate";
          showErrorAlert(`Failed to ${action} voucher template`);
        }
      } catch (err) {
        showErrorAlert("Error updating template status: " + err.message);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleBack = () => {
    navigate("/admin/dashboard");
  };

  return (
    <div className="admin-voucher-redeem-container">
      {/* Back to Dashboard */}
      <div className="admin-voucher-redeem-header">
        <button className="admin-voucher-redeem-back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-voucher-redeem-title">
        <h1>Voucher Management - Redeem</h1>
      </div>

      {/* Create Template Button */}
      <div className="admin-voucher-redeem-top-actions">
        <button
          className="admin-voucher-redeem-add-btn"
          onClick={handleAddClick}
        >
          <i className="fas fa-plus-circle"></i> Create New Template
        </button>
      </div>

      {/* Template Search Bar */}
      <div className="admin-voucher-redeem-search-container">
        <div className="admin-voucher-redeem-search-row">
          <div className="admin-voucher-redeem-search-group">
            <label htmlFor="templateSearch">Search</label>
            <input
              id="templateSearch"
              type="text"
              placeholder="Search templates by name or description..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              className="admin-voucher-redeem-search-input"
            />
          </div>
          <div className="admin-voucher-redeem-search-group">
            <label htmlFor="statusFilter">Status</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-voucher-redeem-filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="admin-voucher-redeem-search-actions">
            <button
              className="admin-voucher-redeem-reset-btn"
              onClick={resetSearchFilters}
            >
             Search
            </button>
          </div>
        </div>
      </div>

      {/* Display fetch errors */}
      {error && !loading && (
        <div className="admin-voucher-redeem-error-container">
          <div className="admin-voucher-redeem-error-message">
            {error}
          </div>
        </div>
      )}

      {/* Modal Section */}
      {showModal && (
        <div className="admin-voucher-redeem-modal-overlay">
          <div className="admin-voucher-redeem-modal">
            <div className="admin-voucher-redeem-modal-header">
              <h2>{isAddMode ? "Create New Voucher Template" : "Edit Voucher Template"}</h2>
              <button
                className="admin-voucher-redeem-modal-close"
                onClick={handleCancelClick}
              >
                &times;
              </button>
            </div>
            <div className="admin-voucher-redeem-modal-content">
              <form onSubmit={handleSubmit} className="admin-voucher-redeem-form">
                {formError && (
                  <div className="admin-voucher-redeem-error-message">
                    {formError}
                  </div>
                )}

                <div className="admin-voucher-redeem-form-group">
                  <label htmlFor="name">
                    Template Name
                    <span className="admin-voucher-redeem-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., RM10 Off Voucher"
                    required
                  />
                </div>
                <div className="admin-voucher-redeem-form-group">
                  <label htmlFor="description">
                    Description
                    <span className="admin-voucher-redeem-required">*</span>
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what this voucher offers..."
                    required
                  />
                </div>
                <div className="admin-voucher-redeem-form-row">
                  <div className="admin-voucher-redeem-form-group">
                    <label htmlFor="discountAmount">
                      Discount Amount (RM)
                      <span className="admin-voucher-redeem-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="discountAmount"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      step="0.5"
                      min="0"
                      placeholder="Enter amount in RM"
                      required
                    />
                  </div>
                  <div className="admin-voucher-redeem-form-group">
                    <label htmlFor="pointsRequired">
                      Points Required
                      <span className="admin-voucher-redeem-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="pointsRequired"
                      value={pointsRequired}
                      onChange={(e) => setPointsRequired(e.target.value)}
                      min="1"
                      placeholder="Enter points needed"
                      required
                    />
                  </div>
                </div>
                <div className="admin-voucher-redeem-form-row">
                  <div className="admin-voucher-redeem-form-group">
                    <label htmlFor="minimumSpendAmount">Minimum Spend Amount (RM)</label>
                    <input
                      type="number"
                      id="minimumSpendAmount"
                      value={minimumSpendAmount}
                      onChange={(e) => setMinimumSpendAmount(e.target.value)}
                      step="0.5"
                      min="0"
                      placeholder="Optional minimum spend"
                    />
                    <span className="admin-voucher-redeem-help-text">
                      Leave empty for no minimum spend requirement
                    </span>
                  </div>
                  <div className="admin-voucher-redeem-form-group">
                    <label htmlFor="expiryDays">
                      Expiry Days
                      <span className="admin-voucher-redeem-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="expiryDays"
                      value={expiryDays}
                      onChange={(e) => setExpiryDays(e.target.value)}
                      min="1"
                      max="365"
                      placeholder="Enter expiry days"
                      required
                    />
                    <span className="admin-voucher-redeem-help-text">
                      Number of days from redemption until voucher expires (1-365 days)
                    </span>
                  </div>
                </div>

                <div className="admin-voucher-redeem-form-actions">
                  <button
                    type="button"
                    className="admin-voucher-redeem-cancel-btn"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="admin-voucher-redeem-save-btn">
                    {isAddMode ? "Create Template" : "Update Template"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      {loading ? (
        <div className="admin-voucher-redeem-loading">
          <i className="fas fa-spinner fa-spin"></i> Loading voucher templates...
        </div>
      ) : (
        <div className="admin-voucher-redeem-table-container">
          {filteredTemplates.length === 0 ? (
            <div className="admin-voucher-redeem-empty-container">
              <i className="fas fa-ticket-alt"></i>
              <p>No voucher templates found matching your criteria.</p>
            </div>
          ) : (
            <table className="admin-voucher-redeem-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Discount</th>
                  <th>Points Required</th>
                  <th>Min Spend</th>
                  <th>Expiry Days</th>
                  <th>Total Redeemed</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr
                    key={template.templateId}
                    className={!template.isActive ? "inactive-template" : ""}
                  >
                    <td className="admin-voucher-redeem-name-cell">
                      {template.name}
                    </td>
                    <td className="admin-voucher-redeem-description-cell">
                      {template.description}
                    </td>
                    <td>
                      RM{template.discountAmount.toFixed(2)}
                    </td>
                    <td>
                      <span className="admin-voucher-redeem-points-badge">
                        {template.pointsRequired} pts
                      </span>
                    </td>
                    <td>
                      <span className={`admin-voucher-redeem-min-spend ${!template.minimumSpendAmount ? 'none' : ''}`}>
                        {template.minimumSpendAmount
                          ? `RM${template.minimumSpendAmount.toFixed(2)}`
                          : 'None'}
                      </span>
                    </td>
                    <td>
                      <span className="admin-voucher-redeem-expiry-days">
                        {template.expiryDays || 30} days
                      </span>
                    </td>
                    <td>
                      <span className="admin-voucher-redeem-redemption-count">
                        {template.totalRedemptions}
                      </span>
                    </td>
                    <td>{formatDate(template.createdAt)}</td>
                    <td>
                      <span
                        className={`admin-voucher-redeem-status-badge ${template.isActive ? "active" : "inactive"
                          }`}
                      >
                        {template.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-voucher-redeem-action-buttons">
                        <button
                          className="admin-voucher-redeem-edit-btn"
                          onClick={() => handleEditClick(template)}
                          disabled={!template.isActive}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className={
                            template.isActive
                              ? "admin-voucher-redeem-deactivate-btn"
                              : "admin-voucher-redeem-activate-btn"
                          }
                          onClick={() =>
                            handleToggleActive(
                              template.templateId,
                              template.isActive
                            )
                          }
                        >
                          <i
                            className={`fas fa-${template.isActive ? "ban" : "check-circle"
                              }`}
                          ></i>
                          {template.isActive ? "Deactivate" : "Reactivate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminVoucherRedeem;